import { useEffect, useRef, useState } from 'react';

import { useUnreadNotifications } from '@/hooks/useNotifications';
import type { AppNotification } from '@/types';

/** Browser notification permission, normalised for SSR/unsupported environments. */
export type WebNotificationPermission = NotificationPermission | 'unsupported';

/** Max number of OS notifications to raise in a single poll cycle (avoid flooding). */
const MAX_ALERTS_PER_CYCLE = 3;

/** localStorage key prefix for the per-user high-water mark. */
const HIGH_WATER_PREFIX = 'webnotif:lastAlertedAt:';

/** True when the Notification API is available in this browser. */
export function isWebNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/** Read the current permission, collapsing unsupported browsers to a sentinel. */
export function readWebNotificationPermission(): WebNotificationPermission {
  return isWebNotificationSupported() ? Notification.permission : 'unsupported';
}

function highWaterKey(userId: string): string {
  return `${HIGH_WATER_PREFIX}${userId}`;
}

function readHighWater(userId: string): number {
  try {
    const raw = window.localStorage.getItem(highWaterKey(userId));
    const parsed = raw ? Date.parse(raw) : NaN;
    return Number.isNaN(parsed) ? 0 : parsed;
  } catch {
    return 0;
  }
}

function writeHighWater(userId: string, isoTimestamp: string): void {
  try {
    window.localStorage.setItem(highWaterKey(userId), isoTimestamp);
  } catch {
    // Private mode / storage disabled — degrade silently; dedup just won't persist.
  }
}

function raiseOsNotification(item: AppNotification): void {
  try {
    const notification = new Notification(item.title, {
      body: item.body,
      // `tag` collapses duplicates if the same item somehow fires twice.
      tag: item.id,
      icon: '/favicon.svg',
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch {
    // Some browsers throw if constructed without an active document focus
    // or when the API is partially unsupported — ignore, in-app bell still works.
  }
}

/**
 * Bridges the in-app notifications feed to native browser (OS) notifications.
 *
 * While the user is authenticated and has granted permission, any unread
 * notification newer than the last one we alerted on raises a desktop
 * notification. Dedup is by a per-user "last alerted" high-water mark
 * persisted in localStorage, so reloads and multiple tabs do not re-fire
 * past alerts. On first run for a user we seed the high-water mark to the
 * newest current item so existing unread items are not replayed.
 *
 * This is the client-only ("Level 1") notifier — it adds no new network
 * traffic because it reads from the shared {@link useUnreadNotifications}
 * query. Real-time delivery while the tab is closed is out of scope here
 * and is covered by the Level 2 Web Push proposal.
 *
 * @param userId - The authenticated user's id, used to scope dedup state.
 */
export function useWebNotifications(userId: string): WebNotificationPermission {
  const [permission, setPermission] = useState<WebNotificationPermission>(
    readWebNotificationPermission,
  );
  const seededRef = useRef(false);
  const { data } = useUnreadNotifications();

  // Keep local permission state in sync if it changes elsewhere (e.g. the
  // activation prompt) while this hook is mounted.
  useEffect(() => {
    setPermission(readWebNotificationPermission());
  }, []);

  // Reset the seed flag when the user changes so a new login re-seeds.
  useEffect(() => {
    seededRef.current = false;
  }, [userId]);

  // For users who granted permission in a previous session, (re-)register this
  // browser for web push on mount. registerWebPush is idempotent and a no-op
  // when push is not configured, so this is safe to run on every load.
  useEffect(() => {
    if (permission !== 'granted' || !userId) return;
    void import('@/lib/push').then(({ registerWebPush }) => registerWebPush());
  }, [permission, userId]);

  useEffect(() => {
    if (permission !== 'granted' || !userId) return;
    const items = data?.data;
    if (!items || items.length === 0) return;

    const previousHighWater = readHighWater(userId);
    const newestTimestamp = items.reduce(
      (max, item) => Math.max(max, Date.parse(item.createdAt) || 0),
      0,
    );

    // First cycle for this user (no stored mark): seed and do not replay.
    if (previousHighWater === 0 && !seededRef.current) {
      seededRef.current = true;
      if (newestTimestamp > 0) {
        writeHighWater(userId, new Date(newestTimestamp).toISOString());
      }
      return;
    }
    seededRef.current = true;

    const fresh = items
      .filter((item) => (Date.parse(item.createdAt) || 0) > previousHighWater)
      .sort((a, b) => (Date.parse(a.createdAt) || 0) - (Date.parse(b.createdAt) || 0));

    if (fresh.length === 0) return;

    fresh.slice(-MAX_ALERTS_PER_CYCLE).forEach(raiseOsNotification);
    if (newestTimestamp > previousHighWater) {
      writeHighWater(userId, new Date(newestTimestamp).toISOString());
    }
  }, [data, permission, userId]);

  return permission;
}
